import { createContext, ReactNode, useContext, useId, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

import { ActionTooltip } from "../../components/Tooltips";

export type ChartFormat = "square" | "landscape";

// Lets a chart adapt its layout to the fullscreen copy without prop drilling.
const ChartCardContext = createContext<{
  inModal: boolean;
  format: ChartFormat;
}>({ inModal: false, format: "landscape" });

export const useChartCard = () => useContext(ChartCardContext);

/**
 * Standard cockpit chart card layout: a fixed-height header (title + optional
 * tabs on the left, optional actions on the right, plus a fullscreen button),
 * a divider, and a padded body. Shared by the analyse charts and the
 * sales-state chart so their headers stay identical (size, font, spacing).
 * Does not render the surrounding `Paper` — the caller wraps it (AnalyseTab
 * wraps each analyse chart; the sales-state chart supplies its own Paper).
 *
 * Fullscreen pops the whole card into a wide Dialog, the Reporting pattern:
 * the content is rendered a second time inside the modal, and stays fully
 * interactive since its state lives in the calling chart component.
 */
const ChartCard = ({
  title,
  tabs,
  actions,
  fullscreen = true,
  children,
  format = "landscape",
}: {
  title: ReactNode;
  tabs?: ReactNode;
  actions?: ReactNode;
  // Off for a card already inside a dialog, where a second one would stack.
  fullscreen?: boolean;
  children: ReactNode;
  // Shape of the fullscreen copy; square charts get a narrower dialog.
  format?: ChartFormat;
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const modalTitleId = useId();

  const content = (inModal: boolean) => (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          p: 1,
          // Keeps the header clear of the modal's absolute close button.
          pr: inModal ? 5 : 1,
          minHeight: 40,
          boxSizing: "border-box",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            // Title and tab labels differ in size and the tabs carry a bottom
            // underline, so centring the boxes misaligns the texts by ~2px.
            alignItems: "baseline",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography
            id={inModal ? modalTitleId : undefined}
            variant="caption"
            sx={{ fontSize: "0.8125rem" }}
          >
            {title}
          </Typography>
          {tabs}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {actions}
          {fullscreen && !inModal && (
            <ActionTooltip title="FULLSCREEN">
              <IconButton
                size="small"
                onClick={() => setIsFullscreen(true)}
                aria-label="enter fullscreen"
                sx={{ color: "text.accent" }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </ActionTooltip>
          )}
        </Box>
      </Box>

      <Divider sx={{ backgroundColor: "divider", height: "1px" }} />

      <Box sx={{ p: 1 }}>
        <ChartCardContext.Provider value={{ inModal, format }}>
          {children}
        </ChartCardContext.Provider>
      </Box>
    </Box>
  );

  if (!fullscreen) return content(false);

  return (
    <>
      {content(false)}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={format === "square" ? "md" : "xl"}
        fullWidth
        aria-labelledby={modalTitleId}
      >
        <IconButton
          aria-label="close fullscreen"
          onClick={() => setIsFullscreen(false)}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 1 }}>{content(true)}</DialogContent>
      </Dialog>
    </>
  );
};

export default ChartCard;
